import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { sanityClient } from '../sanityClient';
import { PortableText, PortableTextComponents } from '@portabletext/react';
import imageUrlBuilder from '@sanity/image-url';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { Helmet } from 'react-helmet-async';
import { AboutFaqSection } from '@/components/about/AboutFaqSection';
import { AboutFooter } from '@/components/about/AboutFooter';
import { RecommendedBlogs } from '@/components/blog/RecommendedBlogs';
import { ShareButtons } from '@/components/blog/ShareButtons';
import { OtherPagesHeader } from '@/components/about/OtherPagesHeader';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AuthorBioModal } from '@/components/blog/AuthorBioModal';
import { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';
import MobileBottomNav from '@/components/dashboard/MobileBottomNav';
import { QrCode, ArrowRight } from 'lucide-react';
import { StickerPacksDrawer } from '@/components/stickers/StickerPacksDrawer';

// Safe way to check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Define a common interface for both Blog and BlogPost
interface BasePost {
  _id?: string;
  title: string;
  slug?: { current: string };
  excerpt?: string;
  mainImage?: {
    asset: {
      url: string;
      _ref?: string;
    }
  };
  author?: {
    name: string;
    image?: {
      asset: {
        url: string;
        _ref?: string;
      }
    };
    bio?: string;
    slug?: { current: string };
  };
  categories?: { title: string }[];
  publishedAt?: string;
}

// Extend BasePost for BlogPost specific fields
interface Post extends BasePost {
  body: any;
}

// Use Vite's import.meta.env for environment variables
const SANITY_PROJECT_ID = import.meta.env.VITE_SANITY_PROJECT_ID || "025rxk3o";
const SANITY_DATASET = import.meta.env.VITE_SANITY_DATASET || 'production';

const builder = imageUrlBuilder({
  projectId: SANITY_PROJECT_ID,
  dataset: SANITY_DATASET,
});

// Helper function to get image builder
function getImageBuilder(source: any): ImageUrlBuilder {
  return builder.image(source);
}

// Function to get standard image URL
function getImageUrl(source: any): string {
  return getImageBuilder(source).auto('format').url();
}

// Convert Sanity Portable Text blocks to a simple Markdown string
function portableTextToMarkdown(blocks: any[]): string {
  if (!Array.isArray(blocks)) return '';

  const lines: string[] = [];

  const spanToMarkdown = (span: any, markDefs: any[]): string => {
    let text = span?.text || '';
    const marks: string[] = span?.marks || [];

    if (!marks.length) return text;

    marks.forEach((mark) => {
      if (mark === 'strong') {
        text = `**${text}**`;
      } else if (mark === 'em') {
        text = `*${text}*`;
      } else {
        const def = markDefs?.find((d: any) => d?._key === mark);
        if (def?._type === 'link' && def?.href) {
          text = `[${text}](${def.href})`;
        }
      }
    });

    return text;
  };

  for (const block of blocks) {
    if (!block) continue;

    // Text blocks (paragraphs, headings, lists)
    if (block._type === 'block') {
      const markDefs = block.markDefs || [];
      const text = (block.children || [])
        .map((child: any) => spanToMarkdown(child, markDefs))
        .join('');

      if (!text.trim()) {
        lines.push('');
        continue;
      }

      // List items
      if (block.listItem) {
        const prefix = block.listItem === 'number' ? '1.' : '-';
        lines.push(`${prefix} ${text}`);
        continue;
      }

      // Headings and normal text
      switch (block.style) {
        case 'h1':
          lines.push(`# ${text}`);
          break;
        case 'h2':
          lines.push(`## ${text}`);
          break;
        case 'h3':
          lines.push(`### ${text}`);
          break;
        case 'h4':
          lines.push(`#### ${text}`);
          break;
        case 'blockquote':
          lines.push(`> ${text}`);
          break;
        default:
          lines.push(text);
          break;
      }

      lines.push('');
      continue;
    }

    // Inline image blocks
    if (block._type === 'image') {
      try {
        const url = getImageUrl(block);
        lines.push(`![${block.alt || 'Image'}](${url})`, '');
      } catch {
        // Ignore broken image conversions
      }
      continue;
    }

    // Code blocks
    if (block._type === 'code') {
      const language = block.language || '';
      const code = block.code || '';
      lines.push('```' + language, code, '```', '');
      continue;
    }
  }

  return lines.join('\n');
}

// Function to get OG image URL
function getOGImageUrl(source: any): string {
  return getImageBuilder(source)
    .width(1200)
    .height(630)
    .format('jpg')
    .url();
}

const components: PortableTextComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) {
        return <div className="my-8 text-red-500">[Image not available]</div>;
      }

      const align = value.align || 'center';
      const size = value.size || 'md';

      let maxWidth: number | undefined;
      if (size === 'sm') maxWidth = 480;
      if (size === 'md') maxWidth = 720;
      if (size === 'lg') maxWidth = 1024;

      let containerClass = 'my-12';
      if (align === 'full') containerClass += ' w-full';
      if (align === 'center') containerClass += ' flex justify-center';
      if (align === 'left') containerClass += ' flex justify-start';
      if (align === 'right') containerClass += ' flex justify-end';

      return (
        <div className={containerClass}>
          <img
            src={getImageUrl(value)}
            alt={value.alt || 'Blog content image'}
            className="rounded-2xl shadow-xlshadow-gray-900/50 object-contain"
            style={{ maxWidth: maxWidth ? `${maxWidth}px` : '100%', width: '100%' }}
          />
        </div>
      );
    },
    code: ({ value }) => {
      const { language, code, filename } = value || {};

      const handleCopy = () => {
        if (typeof navigator !== 'undefined' && navigator.clipboard && code) {
          navigator.clipboard.writeText(code).catch(() => {
            // Silently ignore copy failures
          });
        }
      };

      return (
        <div className="my-8 rounded-xl border border-border bg-muted/60 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-muted/80 border-b border-border/60">
            <span className="text-xs font-mono text-foreground/80">
              {filename || language || 'code'}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="text-xs px-2 py-1 rounded-md border border-border text-foreground/80 hover:bg-background transition-colors"
            >
              Copy
            </button>
          </div>
          <pre className="p-4 overflow-x-auto max-w-full text-sm leading-relaxed bg-background text-foreground/90">
            <code className={language ? `language-${language}` : undefined}>
              {code}
            </code>
          </pre>
        </div>
      );
    },
  },
  block: {
    h1: ({ children }) => <h1 className="text-4xl md:text-5xl font-bold text-foreground mt-12 mb-6 leading-tight tracking-tight">{children}</h1>,
    h2: ({ children }) => <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-10 mb-5 leading-tight tracking-tight">{children}</h2>,
    h3: ({ children }) => <h3 className="text-2xl md:text-3xl font-semibold text-foreground mt-8 mb-4 leading-tight">{children}</h3>,
    normal: ({ children }) => <p className="mb-7 text-lg md:text-xl leading-relaxed text-foreground">{children}</p>,
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc ml-5 mb-4">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal ml-5 mb-4">
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="mb-1">{children}</li>,
    number: ({ children }) => <li className="mb-1">{children}</li>,
  },
  marks: {
    link: ({ value, children }) => {
      const href = value?.href || '#';
      const isExternal = href.startsWith('http');
      return (
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="text-primary underline-offset-2 hover:underline"
        >
          {children}
        </a>
      );
    },
    colorMark: ({ value, children }) => {
      const color = value?.color || 'inherit';
      return <span style={{ color }}>{children}</span>;
    },
  },
};

// For server-side rendering
export async function fetchBlogPost(slug: string): Promise<Post | null> {
  try {
    const post = await sanityClient.fetch(`
      *[_type == "post" && slug.current == $slug][0]{
        title,
        body,
        excerpt,
        mainImage{
          asset->{
            url,
            _ref
          }
        },
        author->{
          name,
          image{
            asset->{
              url,
              _ref
            }
          },
          bio,
          role
        },
        categories[]->{
          title
        },
        publishedAt
      }
    `, { slug });
    return post;
  } catch (error) {
    console.error("Error fetching blog post:", error);
    throw error;
  }
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAuthorModalOpen, setIsAuthorModalOpen] = useState(false);
  const [isStickerDrawerOpen, setIsStickerDrawerOpen] = useState(false);
  const [isMarkdownCopied, setIsMarkdownCopied] = useState(false);

  // Check for server-side rendered data
  const initialData = !isBrowser ? undefined : window.__INITIAL_DATA__;
  const initialPost = initialData?.blogPost as Post | undefined;

  // Use React Query to manage blog post data
  const { 
    data: post,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery<Post, Error>({
    queryKey: ['blogPost', slug],
    queryFn: () => fetchBlogPost(slug || ''),
    enabled: !!slug && !initialPost,
    staleTime: 1000 * 60 * 5,
    retry: 2,
    refetchOnMount: false,
    initialData: initialPost
  });

  // Handle retry - MOVED HERE to ensure consistent hook order
  const handleRetry = React.useCallback(() => {
    refetch();
  }, [refetch]);

  // Get the current post data or use defaults
  const currentPost = post || initialPost;

  const handleCopyMarkdown = React.useCallback(() => {
    if (!currentPost) return;

    const sections: string[] = [];

    // Add YAML frontmatter so the copied content fully represents the page
    const frontmatterLines: string[] = ['---'];

    if (currentPost.title) {
      frontmatterLines.push(`title: "${currentPost.title.replace(/"/g, '\\"')}"`);
    }

    if (slug) {
      frontmatterLines.push(`slug: "${slug.replace(/"/g, '\\"')}"`);
    }

    if (currentPost.excerpt) {
      frontmatterLines.push(`excerpt: "${currentPost.excerpt.replace(/"/g, '\\"')}"`);
    }

    if (currentPost.publishedAt) {
      frontmatterLines.push(`date: "${currentPost.publishedAt}"`);
    }

    if (currentPost.author?.name) {
      frontmatterLines.push(`author: "${currentPost.author.name.replace(/"/g, '\\"')}"`);
    }

    if (currentPost.categories && currentPost.categories.length > 0) {
      frontmatterLines.push('categories:');
      currentPost.categories.forEach((cat) => {
        if (cat?.title) {
          frontmatterLines.push(`  - "${cat.title.replace(/"/g, '\\"')}"`);
        }
      });
    }

    if (currentPost.mainImage?.asset?.url) {
      frontmatterLines.push(`featuredImage: "${currentPost.mainImage.asset.url.replace(/"/g, '\\"')}"`);
    }

    frontmatterLines.push('---', '');
    sections.push(...frontmatterLines);

    // Add main article content
    if (currentPost.body) {
      sections.push(portableTextToMarkdown(currentPost.body));
    }

    const markdownContent = sections.join('\n');

    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(markdownContent)
        .then(() => {
          setIsMarkdownCopied(true);
          window.setTimeout(() => setIsMarkdownCopied(false), 2000);
        })
        .catch(() => {
          // Silently ignore copy failures
        });
    }
  }, [currentPost, slug, setIsMarkdownCopied]);

  // Default meta values for SEO
  const defaultTitle = "Metefy Blog";
  const defaultDescription = "Read the latest updates and stories from Metefy's lost and found platform.";
  const defaultImage = '/og-image.png';
  const siteUrl = 'https://metefy.com';

  // Determine meta content based on post data
  const metaTitle = currentPost?.title ? `${currentPost.title} | Metefy Blog` : defaultTitle;
  const metaDescription = currentPost?.excerpt || (currentPost?.title ? `Read ${currentPost.title} on Metefy Blog` : defaultDescription);
  const metaImage = currentPost?.mainImage?.asset?.url || `${siteUrl}${defaultImage}`;
  const canonicalUrl = `${siteUrl}/blog/${slug}`;

  // Early return for loading state without initial data
  if (isLoading && !initialPost) {
    return (
      <>
        <Helmet prioritizeSeoTags>
          <title>{defaultTitle}</title>
          <meta name="description" content={defaultDescription} />
          <link rel="canonical" href={`https://www.metefy.com/blog/${slug}`} />
          <meta property="og:title" content={defaultTitle} />
          <meta property="og:description" content={defaultDescription} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://www.metefy.com/blog/${slug}`} />
          <meta property="og:image" content="https://www.metefy.com/og-image.png" />
          <meta property="og:site_name" content="Metefy Blog" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={defaultTitle} />
          <meta name="twitter:description" content={defaultDescription} />
          <meta name="twitter:image" content="https://www.metefy.com/og-image.png" />
        </Helmet>
        <div className="min-h-screen bg-background">
          <OtherPagesHeader hideOnScrollDown={true} />
          <div className="container mx-auto px-4 py-20 bg-background">
            <div className="text-center">
              <div className="animate-pulse">
                <div className="h-72 bg-muted rounded-3xl mb-8"></div>
                <div className="h-8 bg-muted w-3/4 mx-auto rounded mb-4"></div>
                <div className="h-4 bg-muted w-1/2 mx-auto rounded mb-8"></div>
                <div className="space-y-4">
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded"></div>
                </div>
              </div>
            </div>
          </div>
          <AboutFooter />
        </div>
      </>
    );
  }

  // If we have no post data at all, show error
  if (!currentPost && !isLoading) {
    return (
      <>
        <Helmet prioritizeSeoTags>
          <title>Post Not Found | Metefy Blog</title>
          <meta name="description" content="The requested blog post could not be found." />
          <link rel="canonical" href={`https://www.metefy.com/blog/${slug}`} />
          <meta property="og:title" content="Post Not Found | Metefy Blog" />
          <meta property="og:description" content="The requested blog post could not be found." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://www.metefy.com/blog/${slug}`} />
          <meta property="og:image" content="https://www.metefy.com/og-image.png" />
          <meta property="og:site_name" content="Metefy Blog" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Post Not Found | Metefy Blog" />
          <meta name="twitter:description" content="The requested blog post could not be found." />
          <meta name="twitter:image" content="https://www.metefy.com/og-image.png" />
        </Helmet>
        <div className="min-h-screen bg-white">
          <OtherPagesHeader />
          <div className="container mx-auto px-4 py-20 bg-gray-50">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-red-500 mb-4">Blog Post Not Found</h1>
              <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
              <Link to="/blog" className="text-sky-700 hover:underline">
                ← Back to Blog
              </Link>
            </div>
          </div>
          <AboutFooter />
        </div>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Helmet prioritizeSeoTags>
          <title>Error | Metefy Blog</title>
          <meta name="description" content="An error occurred while loading the blog post." />
          <link rel="canonical" href={`https://www.metefy.com/blog/${slug}`} />
          <meta property="og:title" content="Error | Metefy Blog" />
          <meta property="og:description" content="An error occurred while loading the blog post." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://www.metefy.com/blog/${slug}`} />
          <meta property="og:image" content="https://www.metefy.com/og-image.png" />
          <meta property="og:site_name" content="Metefy Blog" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Error | Metefy Blog" />
          <meta name="twitter:description" content="An error occurred while loading the blog post." />
          <meta name="twitter:image" content="https://www.metefy.com/og-image.png" />
        </Helmet>
        <div className="min-h-screen bg-background">
          <OtherPagesHeader hideOnScrollDown={true} />
          <div className="container mx-auto px-4 py-20 bg-background">
            <div className="text-center">
              <p className="text-destructive text-lg mb-2">Error loading blog post</p>
              <p className="text-sm text-muted-foreground mb-4">{error instanceof Error ? error.message : 'Please try again later'}</p>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
          <AboutFooter />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{`${currentPost.title} | Metefy Blog`}</title>
        <meta name="description" content={currentPost.excerpt || `Read ${currentPost.title} on Metefy Blog`} />
        <link rel="canonical" href={`https://www.metefy.com/blog/${slug}`} />
        
        {/* Open Graph meta tags */}
        <meta property="og:title" content={`${currentPost.title} | Metefy Blog`} />
        <meta property="og:description" content={currentPost.excerpt || `Read ${currentPost.title} on Metefy Blog`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.metefy.com/blog/${slug}`} />
        <meta property="og:image" content={currentPost.mainImage?.asset?.url || 'https://www.metefy.com/og-image.png'} />
        <meta property="og:site_name" content="Metefy Blog" />

        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${currentPost.title} | Metefy Blog`} />
        <meta name="twitter:description" content={currentPost.excerpt || `Read ${currentPost.title} on Metefy Blog`} />
        <meta name="twitter:image" content={currentPost.mainImage?.asset?.url || 'https://www.metefy.com/og-image.png'} />

        {/* Article specific meta tags */}
        {currentPost.publishedAt && (
          <meta property="article:published_time" content={currentPost.publishedAt} />
        )}
        {currentPost.author?.name && (
          <meta property="article:author" content={currentPost.author.name} />
        )}
        {currentPost.categories?.map((cat, idx) => (
          <meta key={`cat-${idx}`} property="article:tag" content={cat.title} />
        ))}
      </Helmet>

      <div className="min-h-screen bg-background overflow-x-hidden">
        <OtherPagesHeader hideOnScrollDown={true} />
        <section className="py-20 bg-background pt-32">
          <div className="w-full mx-auto px-4 lg:px-8 xl:px-[18rem] lg:max-w-none lg:mx-0 relative">
            <div className="flex flex-col xl:flex-row xl:items-start xl:gap-8">
              {/* Left sidebar fixed on extra-large screens */}
              <aside className="hidden xl:block xl:fixed xl:top-24 xl:left-8 xl:w-60 space-y-4 text-sm text-foreground/80">
                {/* QR Stickers promo, reusing imagery from About hero */}
                <div className="rounded-2xl border border-border bg-card/80 overflow-hidden">
                  <div className="relative">
                    <img
                      src="https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627381/Site_Posts_apdspu.png"
                      alt="Metefy QR Stickers"
                      className="w-full h-32 object-cover object-center"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                  <div className="p-4 space-y-2">
                    <h2 className="text-base font-semibold text-foreground">
                      Metefy QR Stickers
                    </h2>
                    <p className="text-xs text-foreground/80">
                      Stick these on your phone, laptop, metro card and more so finders can reach you
                      safely without sharing your personal number.
                    </p>
                    <ul className="list-disc ml-4 space-y-1 text-xs text-foreground/75">
                      <li>Instant QR profile for your items</li>
                      <li>Privacy‑first communication</li>
                      <li>Works with metro cards &amp; vehicles</li>
                    </ul>
                    <div className="mt-3 flex items-center gap-2">
                      <img
                        src="https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627381/Site_Posts_apdspu.png"
                        alt="Metefy sticker 1"
                        className="w-10 h-10 rounded-lg object-cover border border-border"
                        loading="lazy"
                      />
                      <img
                        src="https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627368/Site_Posts_2_ast368.png"
                        alt="Metefy sticker 2"
                        className="w-10 h-10 rounded-lg object-cover border border-border"
                        loading="lazy"
                      />
                      <img
                        src="https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627375/Site_Posts_1_gypuwl.png"
                        alt="Metefy sticker 3"
                        className="w-10 h-10 rounded-lg object-cover border border-border"
                        loading="lazy"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsStickerDrawerOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-primary hover:underline"
                    >
                      <QrCode className="w-3 h-3" />
                      Buy Metefy QR Stickers
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* About + quick links */}
                <div className="rounded-2xl border border-border bg-card/80 p-4 space-y-3">
                  <h3 className="text-base font-semibold text-foreground">About Metefy</h3>
                  <p className="text-xs text-foreground/80">
                    Metefy is a QR sticker company that gives products a digital identity. We help you
                    attach smart QR profiles to metro cards, bikes, cars, phones and more.
                  </p>
                  <p className="text-xs text-foreground/80">
                    We also partner with brands to create Metefy × Brand collab QR stickers that advertise
                    their products, apps and websites on metro cards and everyday items.
                  </p>
                  <div className="space-y-1">
                    <Link to="/about" className="block text-primary text-xs hover:underline">
                      What is Metefy?
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        const msg = encodeURIComponent(
                          "Hey! I'm interested in advertising with Metefy QR stickers (metro card, bike/car, phone, brand collab)."
                        );
                        window.open(`https://wa.me/918307562996?text=${msg}`, '_blank', 'noopener,noreferrer');
                      }}
                      className="block text-primary text-xs hover:underline text-left"
                    >
                      Advertise with Metefy QR stickers
                    </button>
                  </div>
                </div>
              </aside>

              {/* Main article column - takes remaining width between sidebars */}
              <div className="w-full xl:flex-1 mx-auto xl:mx-0 max-w-none">
                {currentPost.mainImage?.asset && (
                  <div className="relative mb-10 rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src={getImageUrl(currentPost.mainImage)}
                      alt={currentPost.title}
                      className="w-full max-h-96 md:max-h-[500px] object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  </div>
                )}
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900text-white text-center mt-8 mb-6 leading-tight tracking-tight">
                  {currentPost.title}
                </h1>
                <div className="flex-1 flex flex-col space-y-6">
                  {/* Author, Categories, Date */}
                  <div className="flex flex-wrap items-center justify-center gap-4 mb-6 text-sm">
                    {currentPost.author && (
                      <div className="flex items-center gap-3">
                        {currentPost.author.image?.asset && (
                          <button
                            onClick={() => setIsAuthorModalOpen(true)}
                            className="hover:opacity-80 transition-opacity ring-2 ring-sky-200ring-sky-800 rounded-full"
                          >
                            <img
                              src={getImageUrl(currentPost.author.image)}
                              alt={currentPost.author.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          </button>
                        )}
                        <div className="flex flex-col">
                          <button
                            onClick={() => setIsAuthorModalOpen(true)}
                            className="font-semibold text-gray-900text-white hover:text-sky-700hover:text-sky-400 transition-colors text-left"
                          >
                            {currentPost.author.name}
                          </button>
                        </div>
                      </div>
                    )}
                    {currentPost.publishedAt && (
                      <div className="flex items-center gap-2 text-gray-500text-gray-400">
                        <span className="text-gray-500text-gray-400">
                          {new Date(currentPost.publishedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-center mb-4">
                    <button
                      type="button"
                      onClick={handleCopyMarkdown}
                      className="px-4 py-2 text-xs font-semibold rounded-full border border-border bg-card hover:bg-card/80 text-foreground transition-colors"
                    >
                      {isMarkdownCopied ? 'Copied as Markdown!' : 'Copy as Markdown file'}
                    </button>
                  </div>
                  <article className="prose prose-lg max-w-full mx-auto text-foreground prose-headings:text-foreground prose-headings:font-semibold prose-a:text-primary prose-img:rounded-2xl prose-img:shadow-2xl prose-p:leading-relaxed prose-p:text-lg md:prose-p:text-xl prose-p:mb-7">
                    {currentPost.body ? (
                      <PortableText value={currentPost.body} components={components} />
                    ) : (
                      <p className="text-center text-foreground/80">No content available.</p>
                    )}
                  </article>
                  {(currentPost.categories && currentPost.categories.length > 0) && (
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
                      {currentPost.author?.name && (
                        <span className="font-semibold text-foreground/80">
                          By {currentPost.author.name}
                        </span>
                      )}
                      <span className="text-gray-400text-gray-500">•</span>
                      <span className="bg-whitebg-gray-800 px-4 py-2 rounded-full border border-gray-200border-gray-700 shadow-sm">
                        {currentPost.categories.map((cat, idx) => (
                          <span key={cat.title}>
                            {cat.title}
                            {idx < currentPost.categories.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right sidebar fixed on extra-large screens */}
              <aside className="hidden xl:block xl:fixed xl:top-24 xl:right-8 xl:w-60 space-y-4 text-sm text-foreground/80">
                {/* Recommended blogs (compact) */}
                <div className="rounded-2xl border border-border bg-card/80 p-4">
                  <h2 className="text-base font-semibold mb-2 text-foreground">Recommended blogs</h2>
                  <p className="text-xs text-foreground/75 mb-3">
                    Keep exploring Metefy tips, stories and product guides.
                  </p>
                  <div className="-mx-1">
                    <RecommendedBlogs currentSlug={slug} variant="sidebar" />
                  </div>
                </div>

                {/* Safety / help content */}
                <div className="rounded-2xl border border-border bg-card/80 p-4">
                  <h3 className="text-base font-semibold mb-2 text-foreground">Safety &amp; support</h3>
                  <ul className="list-disc ml-4 space-y-1 text-xs text-foreground/80">
                    <li>Never share passwords or OTP with anyone claiming to be Metefy.</li>
                    <li>Use in‑app messaging instead of sharing your personal phone number.</li>
                    <li>Block and report suspicious messages immediately.</li>
                  </ul>
                  <Link
                    to="/privacy-policy"
                    className="inline-block mt-3 text-primary text-xs font-semibold hover:underline"
                  >
                    Read our privacy &amp; safety policy →
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>
        <section className="py-16 bg-background border-t border-border">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">Share this blog</h2>
            <div className="flex flex-col items-center mb-12">
              <ShareButtons post={currentPost} />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">Recommended Blogs</h2>
            <RecommendedBlogs currentSlug={slug} />
          </div>
        </section>
        {currentPost.author && (
          <AuthorBioModal
            isOpen={isAuthorModalOpen}
            onClose={() => setIsAuthorModalOpen(false)}
            author={currentPost.author}
            urlFor={getImageBuilder}
          />
        )}
        <StickerPacksDrawer
          isOpen={isStickerDrawerOpen}
          setIsOpen={setIsStickerDrawerOpen}
        />
        <AboutFaqSection />
        <AboutFooter />
        {user && <MobileBottomNav />}
      </div>
    </>
  );
};

export default BlogPost;
